import { t } from '@lingui/core/macro';
import { Box, Typography, useTheme } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { useInfiniteIndexQuery } from 'api';
import LabAvatar from 'components/library/media/LabAvatar';
import { useSearchState } from 'hooks/useSearchState';
import { useMemo, useState } from 'react';
import type { Lab } from 'types/models';
import { scrollUtils } from 'utils/scrollUtils';
import { Autocomplete } from '../../../components/Autocomplete';
import { CenteredSpinner } from '../../../components/CenteredSpinner';

type SimpleLab = Pick<Lab, 'id' | 'name' | 'logo_url'>;

export const PharmacySearch = () => {
  const [isQueryEnabled, setIsQueryEnabled] = useState(false);
  const [selectedLab, setSelectedLab] = useState<SimpleLab | null>(null);
  const [_, setSearch, searchDebounced] = useSearchState();
  const navigate = useNavigate();
  const theme = useTheme();

  const query = useInfiniteIndexQuery(
    'pharmacies/labs',
    { query: searchDebounced.trim().length >= 2 ? searchDebounced : undefined },
    { enabled: isQueryEnabled }
  );
  const { data, isPending, isFetchingNextPage } = query;

  const options = useMemo(
    () => (!data ? [] : data.pages.flatMap(({ labs }) => labs as SimpleLab[])),
    [data]
  );

  const handleChange = (lab: SimpleLab | null) => {
    setSelectedLab(lab);
    setSearch('');

    if (!lab) return;

    navigate({ to: '/pharmacy/labs/$id', params: { id: lab.id } });
  };

  return (
    <Autocomplete
      id="pharmacy-lab-search"
      onOpen={() => setIsQueryEnabled(true)}
      loading={isPending || isFetchingNextPage}
      loadingText={<CenteredSpinner />}
      value={selectedLab}
      inputParams={{
        placeholder: t`Chercher un labo`,
        required: true,
        sx: {
          color: theme.palette.navBar.menuDefaultText,
        },
      }}
      options={options}
      renderOption={(option) => (
        <Box display="flex" alignItems="center">
          <LabAvatar lab={option} size={24} sx={{ mr: 1 }} />
          <Typography>{option.name}</Typography>
        </Box>
      )}
      getOptionLabel={(option) => option.name}
      onChange={handleChange}
      onInputChange={setSearch}
      ListboxProps={{ onScroll: scrollUtils.triggerFetchNextPageOnScrollToBottom(query) }}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      isSearchFilteringHandledExternally
      size="small"
      sx={{
        px: 2,
        mb: 2,
        '&:hover fieldset.MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.navBar.searchHoverBorder,
        },
      }}
    />
  );
};
